import { get } from 'lodash';

import { IObservable, Subject } from '~/rage/utils';

import { GuiService } from '../GuiService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IChatChannel, IChatMessage, IChatService } from './chatServiceTypes';

export class ChatService implements IChatService {
	private readonly messages = new Subject<IChatMessage[]>([]);
	private readonly sentMessages = new Subject<string[]>([]);
	private readonly activeChannelId = new Subject('');
	private readonly channelsSubject = new Subject<IChatChannel[]>([]);

	private readonly chatWidth = new Subject(335);
	private readonly chatHeight = new Subject(306);

	private readonly channels = new Map<
		string,
		IChatChannel & { messages: IChatMessage[] }
	>();

	constructor(
		private readonly rpc: RpcService,
		private readonly gui: GuiService,
		private readonly vars: VarsService,
	) {
		(window as any).chatAPI = {
			activate: () => {},
			show: () => {},
		};

		rpc.register('chat:addChannel', (payload: unknown) => {
			const channelId = get(payload, 'id', null);
			if (!channelId) {
				return;
			}

			const channel = this.channels.get(channelId);
			if (!channel) {
				this.channels.set(channelId, {
					id: channelId,
					title: get(payload, 'title', '...'),
					hasUnreadMessages: false,
					messages: [],
				});
			} else {
				const title = get(payload, 'title', null);
				if (title) {
					channel.title = title;
				}
			}

			this.channelsSubject.next(Array.from(this.channels.values()));
			if (!this.channels.has(this.activeChannelId.getValue())) {
				this.activeChannelId.next(this.channels.keys().next().value!);
			}
		});

		rpc.register('chat:removeChannel', (channelId: string) => {
			this.channels.delete(channelId);
			this.channelsSubject.next(Array.from(this.channels.values()));

			if (this.activeChannelId.getValue() === channelId) {
				this.activeChannelId.next('global');
			}
		});

		rpc.register(
			'chat:message',
			(payload: IChatMessage & { channelId: string }) => {
				if (!payload.text) {
					return;
				}
				if (payload.channelId === 'any') {
					payload.channelId = this.activeChannelId.getValue();
				}

				if (!this.channels.has(payload.channelId)) {
					return;
				}

				if (!payload.color) {
					payload.color = {
						r: 255,
						g: 255,
						b: 255,
					};
				}
				if (!payload.text) {
					return;
				}

				const channel = this.channels.get(payload.channelId)!;
				channel.messages.push({
					id: payload.id,
					color: payload.color,
					text: payload.text,
					sender: payload.sender,
				});
				if (channel.id === this.activeChannelId.getValue()) {
					this.messages.next(channel.messages);
					channel.hasUnreadMessages = false;
				} else {
					channel.hasUnreadMessages = true;
				}

				this.channelsSubject.next(Array.from(this.channels.values()));
			},
		);

		rpc.register(
			'chat:deleteMessage',
			(payload: { channelId: string; messageId: string }) => {
				const { channelId, messageId } = payload;

				if (!this.channels.has(channelId)) {
					return;
				}

				const channel = this.channels.get(channelId)!;
				const messageIndex = channel.messages.findIndex(
					(m) => m.id === messageId,
				);

				if (messageIndex === -1) {
					return;
				}

				channel.messages.splice(messageIndex, 1);

				if (channel.id === this.activeChannelId.getValue()) {
					this.messages.next(channel.messages);
				}
			},
		);

		this.channelsSubject.next(Array.from(this.channels.values()));

		this.activeChannelId.subscribe((channelId) => {
			if (!this.channels.has(channelId)) {
				return;
			}

			const channel = this.channels.get(channelId)!;
			this.messages.next(channel.messages);
			channel.hasUnreadMessages = false;
		});
	}

	public getChannels(): IObservable<IChatChannel[]> {
		return this.channelsSubject;
	}

	public setActiveChannelId(channelId: string): void {
		if (!this.channels.has(channelId)) {
			return;
		}

		return this.activeChannelId.next(channelId);
	}

	public getActiveChannelId(): IObservable<string> {
		return this.activeChannelId;
	}

	public getMessages(): IObservable<IChatMessage[]> {
		return this.messages;
	}

	public async sendMessage(message: string): Promise<void> {
		if (!message.length) {
			return;
		}

		try {
			this.rpc.invokeClient('chat:sendMessage', {
				channelId: this.activeChannelId.getValue(),
				message,
			});

			const sentMessages = this.sentMessages.getValue();
			sentMessages.push(message);
		} catch (error) {
			console.error(error);
		}
	}

	public getSentMessages(): IObservable<string[]> {
		return this.sentMessages;
	}

	public async setCursorVisible(visible: boolean): Promise<void> {
		return await this.gui.setCursorVisible('chat', visible);
	}

	public getChatWidth(): IObservable<number> {
		return this.chatWidth;
	}

	public getChatHeight(): IObservable<number> {
		return this.chatHeight;
	}

	public deleteMessage(channelId: string, messageId: string): void {
		if (!this.channels.has(channelId)) {
			return;
		}

		const channel = this.channels.get(channelId)!;
		const messageIndex = channel.messages.findIndex((m) => m.id === messageId);

		if (messageIndex === -1) {
			return;
		}

		channel.messages.splice(messageIndex, 1);

		if (channel.id === this.activeChannelId.getValue()) {
			this.messages.next(channel.messages);
		}
	}
}
